import { Checkbox, FormControlLabel, FormGroup } from '@mui/material';
import { useState, useMemo } from 'react';
import { FilterActionName, useCaseSensitiveSearch, useFilterAction, useWholeWordSearch } from '../../state/FilterContext';
import { TextExtractor, findFirstComponentOfType } from '../internal/TextExtractor';
import { useFilterStateController } from '../../state/filter-state-controller.context';

interface StateBooleanCheckboxProps {
  label?: string,
  state: "case-sensitive-search" | "whole-word-search",
  children: React.ReactNode,
}

/***
 * A component that allows the user to toggle a boolean state.
 * The state is updated in the filter state.
 * The label is optional and can be provided as a child component.
 * The state is specified by the state prop.
 */
export function StateBooleanCheckbox({ label, state, children }: StateBooleanCheckboxProps) {
  const updateCaseSensitiveSearch = useFilterAction(FilterActionName.UPDATE_CASE_SENSITIVE_SEARCH);
  const updateWholeWordSearch = useFilterAction(FilterActionName.UPDATE_WHOLE_WORD_SEARCH);

  const caseSensitiveSearch = useCaseSensitiveSearch();
  const wholeWordSearch = useWholeWordSearch();
  const controller = useFilterStateController();

  // Get the label from the label child
  const labelChild = useMemo(() => findFirstComponentOfType(children, "arg-label"), [children]);
  const [labelValue, setLabelValue] = useState(label || "");

  const setStateValue = (value: boolean) => {
    if (controller) {
      controller.requestChange((stateSnapshot) => {
        const nextState = { ...stateSnapshot };
        switch (state) {
          case "case-sensitive-search":
            nextState.caseSensitiveSearch = value;
            break;
          case "whole-word-search":
            nextState.wholeWordSearch = value;
            break;
          default:
            console.warn("StateBooleanCheckbox: Invalid state", state);
            break;
        }
        return nextState;
      }, { source: `statebooleancheckbox:${state}` });
      return;
    }

    switch (state) {
      case "case-sensitive-search":
        updateCaseSensitiveSearch(value);
        break;
      case "whole-word-search":
        updateWholeWordSearch(value);
        break;
      default:
        console.warn("StateBooleanCheckbox: Invalid state", state);
        break;
    }
  }

  const value = useMemo(() => {
    switch (state) {
      case "case-sensitive-search":
        return caseSensitiveSearch;
      case "whole-word-search":
        return wholeWordSearch;
      default:
        console.warn("StateBooleanCheckbox: Invalid state", state);
        return false;
    }
  }, [state, caseSensitiveSearch, wholeWordSearch]);

  return <>
    {labelChild && (
      <TextExtractor onExtractedText={setLabelValue}>
        {labelChild?.props.children}
      </TextExtractor>
    )}
    <FormGroup>
      <FormControlLabel
        control={<Checkbox checked={value} onChange={() => { setStateValue(!value); }} />}
        label={labelValue}
      />
    </FormGroup>
  </>;
}